// ═════════════════════════════════════════════════════════════════════════════
// Auto tab — NHCX pre-authorization & claim
//
// One form, two modes. Both endpoints take ClaimExtraInput; the only body
// difference is pre_auth_ref (mandatory for claim, ignored for pre-auth).
// Contract: PREAUTH_CLAIM_UI_INTEGRATION.md (repo root).
//
// Three phases in one card: form → waiting (polling) → result.
// Local Field / DisplayField copies match CoverageEligibilityPanel — that page
// imports this component, so sharing those helpers back would be circular.
// ═════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef } from "react";
import { MenuItem } from "@mui/material";
import { C, fmtINR } from "../constants";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdDateField from "../../ipd/utils/IpdDateField";
import IpdButton from "../../ipd/utils/IpdButton";
import { IPD_FIELD_COLORS } from "../../ipd/utils/ipdFieldStyles";
import SvgIcon from "../SvgIcon";
import SnomedSearchField from "./SnomedSearchField";
import Icd10SearchField from "./Icd10SearchField";
import Icd10MappedField from "./Icd10MappedField";
import {
  NHCX_INSURER_REGISTRY,
  NHCX_EMPTY_INSURER,
  NHCX_DUMMY_PAYER_CODE,
  nhcxAppMode,
} from "../nhcx-client";
import {
  submitPreauth,
  submitClaim,
  pollNhcxResponse,
  normalizeSubmitError,
  readGatewayStatus,
  triggerDummyPayer,
} from "../preauth-claim-api";

// ── API vocabulary ───────────────────────────────────────────────────────────

const RELATIONSHIPS = ["self", "spouse", "child", "parent", "common", "other", "injured", "polholder"];
const GENDERS = ["male", "female", "other", "unknown"];
const PRIORITIES = ["normal", "stat", "deferred"];
const QUANTITY_UNITS = ["day", "procedure", "unit", "session"];
const SUPPORT_CATEGORIES = [
  "lab-test",
  "radiology",
  "discharge-summary",
  "prescription",
  "consultation-notes",
  "vital-signs",
  "other",
];

const PREAUTH_STAGES = {
  preauth_initiated: "Request sent",
  ack_queued: "Payer acknowledged, queued",
  query_raised: "Payer wants more information",
  query_ack: "Query response acknowledged",
  query_response: "Query response submitted",
  approved: "Approved",
  denied: "Rejected",
  reprocess_initiated: "Resubmission started",
  reprocess_ack: "Resubmission acknowledged",
};

const CLAIM_STAGES = {
  claim_initiated: "Request sent",
  ack_queued: "Payer acknowledged, queued",
  in_process: "Under evaluation",
  forwarded: "Forwarded internally by the payer",
  query_raised: "Payer wants more information",
  query_response: "Query response submitted",
  doc_query_response: "Document query response submitted",
  approved: "Approved for settlement",
  denied: "Rejected",
};

const OVERRIDE_FIELDS = {
  override_patient_abha_id: {
    label: "Patient ABHA Number",
    hint: "XX-XXXX-XXXX-XXXX — an ABHA Address (user@sbx) is rejected",
  },
  override_patient_aadhaar: { label: "Patient Aadhaar", hint: "12 digits. Either Aadhaar or ABHA is enough." },
  override_patient_phone: { label: "Patient phone", hint: "+91 is added automatically" },
  override_patient_gender: { label: "Patient gender", type: "gender" },
  override_patient_dob: { label: "Patient date of birth", type: "date" },
  override_facility_name: { label: "Facility name" },
  override_facility_abdm_id: { label: "Facility ABDM ID", hint: "ABDM facility ID, e.g. IN3210000017" },
  override_facility_phone: { label: "Facility phone", hint: "+91 is added automatically" },
  override_facility_email: { label: "Facility email" },
  override_doctor_registration_no: { label: "Doctor registration no.", hint: "Medical council registration" },
  override_insurance_policy_no: { label: "Insurance policy no.", hint: "Not the member ID." },
  override_admitted_at: { label: "Admission datetime", type: "datetime", hint: "Sent as IST (+05:30)" },
};

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 20;
const DUMMY_PAYER_WAIT_S = 10;
const SANDBOX_PREAUTH_PLACEHOLDER = "PA-2026-0001";

function sampleDecision(mode) {
  return {
    use_case: mode === "claim" ? "claim" : "preauth",
    status: "complete",
    stage: "approved",
    decision: {
      outcome: "complete",
      disposition: mode === "claim" ? "Approved for settlement" : "Pre-authorisation approved",
      kind: null,
      pre_auth_ref: SANDBOX_PREAUTH_PLACEHOLDER,
      total: [{ category: "eligible", amount: 75000 }],
      items: [{ sequence: 1, adjudication: [{ category: "eligible", amount: 75000 }] }],
      errors: [],
    },
    received_at: "2026-01-01T00:00:00Z",
  };
}

const EMPTY_DX = { icd10_code: "", icd10_display: "" };
const EMPTY_PX = { snomed_code: "", snomed_display: "" };
const EMPTY_ITEM = {
  product_or_service_snomed_code: "",
  product_or_service_snomed_display: "",
  unit_price: "",
  quantity_value: "",
  quantity_unit: "procedure",
  serviced_date: "",
};
const EMPTY_INFO = { category: "lab-test", value_string: "" };

// ── local styles (mirrors the eligibility Auto tab) ──────────────────────────

const cardStyle = {
  background: C.card,
  borderRadius: "12px",
  border: `1px solid ${C.border}`,
  boxShadow: "0 2px 8px rgba(13,35,63,0.07)",
};
const labelStyle = {
  fontSize: "11px",
  fontWeight: "700",
  color: C.textSub,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  display: "block",
  lineHeight: 1.4,
  marginBottom: "6px",
};
const sectionLabelStyle = {
  fontSize: "12px",
  fontWeight: "800",
  color: C.text,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "6px",
};
const sectionHintStyle = {
  fontSize: "12px",
  color: C.textSub,
  marginBottom: "14px",
  lineHeight: 1.45,
};
const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
  columnGap: "24px",
  rowGap: "8px",
  alignItems: "start",
};
const sectionBoxStyle = {
  background: C.bg,
  border: `1px solid ${C.border}`,
  borderRadius: "10px",
  padding: "16px 18px 18px",
};
const routingBoxStyle = {
  marginTop: "16px",
  paddingTop: "14px",
  borderTop: `1px solid ${C.border}`,
};
const innerCardStyle = {
  border: `1px solid ${C.border}`,
  borderRadius: "10px",
  padding: "16px",
  background: "#fff",
  display: "flex",
  flexDirection: "column",
  gap: "14px",
};

function DisplayField({ label, value }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={labelStyle}>{label}</label>
      <IpdTextField
        fullWidth
        disabled
        value={value || "—"}
        title={value || ""}
        sx={{
          "& .MuiOutlinedInput-root.Mui-disabled": {
            boxShadow: IPD_FIELD_COLORS.shadow,
            "& fieldset": { borderColor: IPD_FIELD_COLORS.border },
          },
          "& .MuiOutlinedInput-input.Mui-disabled": {
            WebkitTextFillColor: value ? C.text : C.textSub,
            fontWeight: 600,
          },
        }}
      />
    </div>
  );
}

function Field({ label, required, hint, hintTone, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={labelStyle}>
        {label}
        {required ? " *" : ""}
      </label>
      {children}
      <div
        title={hint || undefined}
        style={{
          fontSize: "11px",
          color: hintTone === "warning" ? C.warning : C.textSub,
          marginTop: "6px",
          minHeight: "16px",
          lineHeight: 1.3,
          fontWeight: hintTone === "warning" ? 600 : 400,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {hint || "\u00a0"}
      </div>
    </div>
  );
}

function Pill({ tone = "grey", children }) {
  const tones = {
    grey: { bg: "#f1f3f5", fg: C.textSub, br: C.border },
    green: { bg: "#e9f7ef", fg: C.success, br: "#b7e4c7" },
    red: { bg: "#fdecec", fg: C.danger, br: "#f5c2c0" },
    blue: { bg: C.accent, fg: C.primary, br: "#bfe3fb" },
    amber: { bg: "#fff8e1", fg: C.warning, br: "#ffe69c" },
  };
  const t = tones[tone] || tones.grey;
  return (
    <span
      style={{
        fontSize: "10px",
        fontWeight: "700",
        padding: "3px 9px",
        borderRadius: "20px",
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.br}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function PreviewBanner() {
  return (
    <div
      style={{
        fontSize: "12px",
        fontWeight: "600",
        color: "#B45309",
        background: "#FFF8E1",
        border: "1px solid #FFE082",
        borderRadius: "8px",
        padding: "8px 12px",
        textAlign: "left",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      Preview — sample data so you can see the next screens. This is not a payer decision.
    </div>
  );
}

function ModeSwitch({ value, onChange, disabled }) {
  const opts = [
    { id: "preauth", label: "Pre-Authorization" },
    { id: "claim", label: "Claim" },
  ];
  return (
    <div
      style={{
        display: "inline-flex",
        gap: "3px",
        padding: "3px",
        background: "#f1f3f5",
        border: `1px solid ${C.border}`,
        borderRadius: "10px",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {opts.map((o) => (
        <button
          key={o.id}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.id)}
          style={{
            border: "none",
            cursor: disabled ? "default" : "pointer",
            fontSize: "12px",
            fontWeight: "700",
            padding: "6px 16px",
            borderRadius: "8px",
            background: value === o.id ? "#fff" : "transparent",
            color: value === o.id ? C.primary : C.textSub,
            boxShadow: value === o.id ? "0 1px 3px rgba(13,35,63,0.14)" : "none",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const isPastDate = (iso) => Boolean(iso) && iso < todayISO();

function toMoney(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.round(x * 100) / 100;
}

function toIstIso(dateStr, timeStr) {
  if (!dateStr) return "";
  const time = timeStr && /^\d{2}:\d{2}/.test(timeStr) ? timeStr.slice(0, 5) : "18:00";
  return `${dateStr}T${time}:00+05:30`;
}

function parseIstIso(iso) {
  if (!iso) return { date: "", time: "18:00" };
  const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return { date: m?.[1] || "", time: m?.[2] || "18:00" };
}

function preauthRefStorageKey(admissionId) {
  return `nhcx_preauth_ref_${admissionId}`;
}

function readStoredPreauthRef(admissionId) {
  if (!admissionId) return "";
  try {
    return sessionStorage.getItem(preauthRefStorageKey(admissionId)) || "";
  } catch {
    return "";
  }
}

function writeStoredPreauthRef(admissionId, ref) {
  if (!admissionId || !ref) return;
  try {
    sessionStorage.setItem(preauthRefStorageKey(admissionId), ref);
  } catch {
    // sessionStorage can throw in private mode — the in-memory field still works.
  }
}

function stageLabel(mode, stage) {
  if (!stage) return "";
  const table = mode === "claim" ? CLAIM_STAGES : PREAUTH_STAGES;
  return table[stage] || stage;
}

function stageTone(stage) {
  if (stage === "approved") return "green";
  if (stage === "denied") return "red";
  if (stage === "query_raised") return "amber";
  if (stage) return "blue";
  return "grey";
}

function gatewayErrorText(error) {
  if (error == null) return "";
  if (typeof error === "string") return error;
  return error.message || error.code || error.error || JSON.stringify(error);
}

function payloadStringsOf(result) {
  const raw =
    result?.payload_strings ||
    result?.decision?.payload_strings ||
    result?.communication?.payload_strings ||
    [];
  return Array.isArray(raw) ? raw.filter(Boolean) : [];
}

function eligibleTotal(decision) {
  const totals = decision?.total;
  if (!Array.isArray(totals)) return null;
  const eligible = totals.find((t) => String(t?.category || "").toLowerCase() === "eligible");
  const amount = eligible?.amount ?? totals[0]?.amount;
  return amount == null ? null : Number(amount);
}

// ── component ────────────────────────────────────────────────────────────────

export default function PreAuthClaimPanel({
  admissionId,
  patient,
  display,
  memberCardNumber,
  insurancePlan,
  mode: modeProp,
  onModeChange,
  hideModeSwitch,
  onCopyPreauthToManual,
  onCopyClaimToManual,
  onContinueToCommunication,
}) {
  const [internalMode, setInternalMode] = useState("preauth"); // preauth | claim
  const isControlled = modeProp === "preauth" || modeProp === "claim";
  const mode = isControlled ? modeProp : internalMode;
  const setMode = (next) => {
    if (isControlled) onModeChange?.(next);
    else setInternalMode(next);
  };
  const [phase, setPhase] = useState("form"); // form | waiting | result

  const [request, setRequest] = useState({
    insurerKey: NHCX_INSURER_REGISTRY.some((i) => i.key === insurancePlan?.insurerKey)
      ? insurancePlan.insurerKey
      : (NHCX_INSURER_REGISTRY[0]?.key || "custom"),
    coverage_subscriber_id: memberCardNumber || "",
    coverage_relationship_code: "self",
    coverage_period_end: "",
    billable_date: "",
    billable_time: "18:00",
    pre_auth_ref: readStoredPreauthRef(admissionId),
    claim_identifier_value: "",
    priority: "normal",
    sub_type: "",
  });
  const [customInsurer, setCustomInsurer] = useState(NHCX_EMPTY_INSURER);
  const [diagnoses, setDiagnoses] = useState([{ ...EMPTY_DX }]);
  const [procedures, setProcedures] = useState([]);
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [supporting, setSupporting] = useState([]);

  const [overrides, setOverrides] = useState({
    override_insurance_policy_no: insurancePlan?.policyNo || display?.policy || "",
  });
  const [revealed, setRevealed] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [correlationId, setCorrelationId] = useState("");
  const [apiCallId, setApiCallId] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [pollExhausted, setPollExhausted] = useState(false);
  const [pollCycle, setPollCycle] = useState(0);
  const [result, setResult] = useState(null);
  const [submittedAt, setSubmittedAt] = useState(0);
  const [preview, setPreview] = useState(false);

  const insurer =
    request.insurerKey === "custom"
      ? customInsurer
      : NHCX_INSURER_REGISTRY.find((i) => i.key === request.insurerKey) || NHCX_EMPTY_INSURER;

  const isClaim = mode === "claim";
  const isDummyPayer = insurer.payer_code === NHCX_DUMMY_PAYER_CODE;
  const isSandbox = String(nhcxAppMode() || "").toLowerCase().includes("sandbox") || isDummyPayer;
  const mapSourceSnomed =
    procedures.find((p) => p.snomed_code)?.snomed_code ||
    items.find((i) => i.product_or_service_snomed_code)?.product_or_service_snomed_code ||
    "";

  const setReq = (key, value) => setRequest((p) => ({ ...p, [key]: value }));
  const setDx = (idx, patch) =>
    setDiagnoses((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  const setPx = (idx, patch) =>
    setProcedures((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  const setItem = (idx, patch) =>
    setItems((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

  const runningTotal = items.reduce(
    (sum, it) => sum + toMoney(toMoney(it.unit_price) * toMoney(it.quantity_value)),
    0
  );

  useEffect(() => {
    if (!memberCardNumber) return;
    setRequest((p) => (p.coverage_subscriber_id ? p : { ...p, coverage_subscriber_id: memberCardNumber }));
  }, [memberCardNumber]);

  useEffect(() => {
    const stored = readStoredPreauthRef(admissionId);
    if (!stored) return;
    setRequest((p) => (p.pre_auth_ref ? p : { ...p, pre_auth_ref: stored }));
  }, [admissionId]);

  // ── polling ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "waiting" || !correlationId || preview) return undefined;
    let cancelled = false;
    let timer;

    const tick = async (n) => {
      try {
        const res = await pollNhcxResponse(correlationId);
        if (cancelled) return;
        if (res?.pending || res?.status === "partial") {
          if (n >= MAX_POLLS) {
            setPollExhausted(true);
            return;
          }
          setAttempts(n);
          timer = setTimeout(() => tick(n + 1), POLL_INTERVAL_MS);
          return;
        }
        setResult(res);
        const issued = res?.decision?.pre_auth_ref;
        if (issued) {
          writeStoredPreauthRef(admissionId, issued);
          setRequest((p) => ({ ...p, pre_auth_ref: issued }));
        }
        setPhase("result");
      } catch (err) {
        if (cancelled) return;
        setError(normalizeSubmitError(err));
        setPollExhausted(true);
      }
    };

    timer = setTimeout(() => tick(1), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [phase, correlationId, pollCycle, admissionId, preview]);

  const validate = () => {
    if (!admissionId || !(Number(admissionId) > 0)) return "This admission has no numeric IPD id.";
    if (!insurer.payer_code) return "Select a payer.";
    if (!insurer.insurer_rohini_id) return "Insurer ROHINI ID is required.";
    if (!insurer.insurer_phone) return "Insurer phone is required.";
    if (!insurer.insurer_email) return "Insurer email is required.";
    if (!insurer.insurer_policy_namespace_uri) return "Insurer policy namespace URI is required.";
    if (!request.coverage_subscriber_id.trim()) return "Member / Subscriber ID is required.";
    if (!request.coverage_period_end) return "Policy valid-until date is required.";
    if (!request.billable_date) return "Expected treatment end date is required.";

    const billableIso = toIstIso(request.billable_date, request.billable_time);
    const admittedIso = overrides.override_admitted_at;
    if (admittedIso && billableIso <= admittedIso) {
      return "Treatment end must be after the admission datetime.";
    }

    if (isClaim && !request.pre_auth_ref.trim()) {
      return "Authorisation reference is required for claim submission — it comes from the approved pre-auth.";
    }

    const filledDx = diagnoses.filter((d) => d.icd10_code.trim() || d.icd10_display.trim());
    if (filledDx.some((d) => d.icd10_display.trim() && !d.icd10_code.trim())) {
      return "Pick a diagnosis from the ICD-10 list — the code comes from the selection.";
    }
    if (!filledDx.some((d) => d.icd10_code.trim() && d.icd10_display.trim())) {
      return "At least one diagnosis is required.";
    }

    const halfPx = procedures.some(
      (p) => (p.snomed_display.trim() && !p.snomed_code.trim()) || (p.snomed_code.trim() && !p.snomed_display.trim())
    );
    if (halfPx) return "Pick a procedure from the search results, or remove the incomplete row.";

    const unresolvedItem = items.some(
      (i) => i.product_or_service_snomed_display.trim() && !i.product_or_service_snomed_code.trim()
    );
    if (unresolvedItem) {
      return "Pick a billing item from the search results — the SNOMED code comes from the selection.";
    }
    const filledItems = items.filter((i) => i.product_or_service_snomed_code.trim());
    if (!filledItems.length) return "At least one billing item is required.";
    if (filledItems.some((i) => !(toMoney(i.unit_price) > 0))) return "Each billing item needs a unit price greater than 0.";
    if (filledItems.some((i) => !(toMoney(i.quantity_value) > 0))) return "Each billing item needs a quantity greater than 0.";

    const halfInfo = supporting.some((s) => s.value_string.trim() && !s.category);
    if (halfInfo) return "Each supporting-info row needs a category.";

    return "";
  };

  const buildPayload = () => {
    const payload = {
      ipd_id: Number(admissionId),
      payer_code: insurer.payer_code,
      insurer_rohini_id: insurer.insurer_rohini_id,
      insurer_phone: insurer.insurer_phone,
      insurer_email: insurer.insurer_email,
      insurer_policy_namespace_uri: insurer.insurer_policy_namespace_uri,
      coverage_subscriber_id: request.coverage_subscriber_id.trim(),
      coverage_relationship_code: request.coverage_relationship_code,
      coverage_period_end: request.coverage_period_end,
      billable_period_end: toIstIso(request.billable_date, request.billable_time),
      diagnoses: diagnoses
        .filter((d) => d.icd10_code.trim() && d.icd10_display.trim())
        .map((d) => ({ icd10_code: d.icd10_code.trim(), icd10_display: d.icd10_display.trim() })),
      items: items
        .filter((i) => i.product_or_service_snomed_code.trim())
        .map((i) => {
          const entry = {
            product_or_service_snomed_code: i.product_or_service_snomed_code.trim(),
            product_or_service_snomed_display: i.product_or_service_snomed_display.trim(),
            unit_price: toMoney(i.unit_price),
            quantity_value: toMoney(i.quantity_value),
            quantity_unit: i.quantity_unit,
          };
          if (i.serviced_date) entry.serviced_date = i.serviced_date;
          return entry;
        }),
    };
    if (insurer.insurer_name) payload.insurer_name = insurer.insurer_name;
    if (request.priority && request.priority !== "normal") payload.priority = request.priority;
    else payload.priority = request.priority || "normal";
    if (request.sub_type.trim()) payload.sub_type = request.sub_type.trim();
    if (request.claim_identifier_value.trim()) payload.claim_identifier_value = request.claim_identifier_value.trim();
    if (isClaim) payload.pre_auth_ref = request.pre_auth_ref.trim();

    const filledPx = procedures.filter((p) => p.snomed_code.trim() && p.snomed_display.trim());
    if (filledPx.length) {
      payload.procedures = filledPx.map((p) => ({
        snomed_code: p.snomed_code.trim(),
        snomed_display: p.snomed_display.trim(),
      }));
    }

    const filledInfo = supporting.filter((s) => s.category && (s.value_string || "").trim());
    if (filledInfo.length) {
      payload.supporting_info = filledInfo.map((s) => ({
        category: s.category,
        value_string: s.value_string.trim(),
      }));
    }

    Object.entries(overrides).forEach(([k, v]) => {
      if (String(v || "").trim()) payload[k] = String(v).trim();
    });
    return payload;
  };

  const handleSubmit = async () => {
    const problem = validate();
    if (problem) {
      setError({ message: problem, fieldErrors: [], missing: [] });
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = buildPayload();
      const res = isClaim ? await submitClaim(payload) : await submitPreauth(payload);
      const gateway = readGatewayStatus(res);
      if (!gateway.queued) {
        setError({
          message:
            gatewayErrorText(gateway.error) ||
            `NHCX rejected the request (${gateway.protocolStatus || "no protocol_status"}).`,
          fieldErrors: [],
          missing: [],
        });
        return;
      }
      const id = res?.correlation_id;
      if (!id) {
        setError({
          message: "The service accepted the request but returned no correlation id.",
          fieldErrors: [],
          missing: [],
        });
        return;
      }
      setCorrelationId(id);
      setApiCallId(res?.api_call_id || "");
      setAttempts(0);
      setPollExhausted(false);
      setResult(null);
      setSubmittedAt(Date.now());
      setPreview(false);
      setPhase("waiting");
    } catch (err) {
      const normalized = normalizeSubmitError(err);
      setError(normalized);
      const asked = [...new Set(normalized.missing.flatMap((m) => m.overrides))];
      if (asked.length) {
        setRevealed((prev) => [...new Set([...prev, ...asked])]);
        setOverrides((prev) => {
          const next = { ...prev };
          asked.forEach((name) => {
            if (next[name] === undefined) {
              next[name] =
                name === "override_patient_gender" ? String(patient?.gender || "").toLowerCase() : "";
            }
          });
          return next;
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = useCallback(() => {
    setPhase("form");
    setResult(null);
    setCorrelationId("");
    setApiCallId("");
    setAttempts(0);
    setPollExhausted(false);
    setError(null);
    setSubmittedAt(0);
    setPreview(false);
  }, []);

  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (prevModeRef.current === mode) return;
    prevModeRef.current = mode;
    handleReset();
  }, [mode, handleReset]);

  const handleSkip = () => {
    setError(null);
    setCorrelationId("preview");
    setApiCallId("");
    setAttempts(0);
    setPollExhausted(false);
    setResult(null);
    setSubmittedAt(Date.now());
    setPreview(true);
    setPhase("waiting");
  };

  const handleSkipToResult = () => {
    const sample = sampleDecision(mode);
    setPreview(true);
    setCorrelationId((id) => id || "preview");
    const issued = sample.decision.pre_auth_ref;
    if (issued) {
      writeStoredPreauthRef(admissionId, issued);
      setRequest((p) => ({ ...p, pre_auth_ref: p.pre_auth_ref || issued }));
    }
    setResult(sample);
    setPhase("result");
  };

  const continueToClaim = () => {
    const issued = result?.decision?.pre_auth_ref || request.pre_auth_ref || SANDBOX_PREAUTH_PLACEHOLDER;
    writeStoredPreauthRef(admissionId, issued);
    setRequest((p) => ({ ...p, pre_auth_ref: issued }));
    setMode("claim");
    handleReset();
  };

  const statusPill =
    phase === "waiting" ? (
      <Pill tone="blue">Waiting for payer</Pill>
    ) : phase === "result" ? (
      <Pill tone={stageTone(result?.stage || result?.status)}>
        {stageLabel(mode, result?.stage) || result?.status || "Answered"}
      </Pill>
    ) : (
      <Pill>{isClaim ? "Claim not submitted" : "Pre-auth not submitted"}</Pill>
    );

  return (
    <div style={cardStyle}>
      <div
        style={{
          padding: "16px 20px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SvgIcon name="shield" size={16} color={C.primary} />
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: C.text }}>
            NHCX {isClaim ? "Claim Submission" : "Pre-Authorization"}
          </h3>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          {!hideModeSwitch && (
            <ModeSwitch value={mode} onChange={setMode} disabled={phase !== "form"} />
          )}
          {statusPill}
        </div>
      </div>

      <div style={{ padding: "20px 20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
        {phase === "waiting" ? (
          <WaitingState
            mode={mode}
            correlationId={correlationId}
            apiCallId={apiCallId}
            attempts={attempts}
            exhausted={pollExhausted}
            error={error}
            submittedAt={submittedAt}
            showDummyPayer={isSandbox && !preview}
            preview={preview}
            onKeepWaiting={() => {
              setPollExhausted(false);
              setError(null);
              setAttempts(0);
              setPollCycle((c) => c + 1);
            }}
            onSkipToResult={handleSkipToResult}
            onStop={handleReset}
          />
        ) : phase === "result" ? (
          <ResultState
            mode={mode}
            result={result}
            correlationId={correlationId}
            apiCallId={apiCallId}
            preAuthRef={request.pre_auth_ref}
            preview={preview}
            onRunAgain={handleReset}
            onContinueToClaim={isClaim ? null : continueToClaim}
            onContinueToCommunication={onContinueToCommunication}
            onCopyPreauthToManual={onCopyPreauthToManual}
            onCopyClaimToManual={onCopyClaimToManual}
            onKeepPolling={() => {
              setPollExhausted(false);
              setError(null);
              setAttempts(0);
              setPhase("waiting");
              setPollCycle((c) => c + 1);
            }}
          />
        ) : (
          <>
            <div style={sectionHintStyle}>
              {isClaim
                ? "Ask the payer to settle this admission. The authorisation number from the approved pre-auth is required and is filled in automatically."
                : "Ask the payer to approve this treatment before it happens. The same form is reused later for the claim."}
            </div>

            {/* ── 1. PAYER / POLICY (read-only, from registration) ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionLabelStyle}>
                <SvgIcon name="building" size={14} color={C.textSub} /> Payer / Policy Information
              </div>
              <div style={gridStyle}>
                <DisplayField label="Payer Type" value={display?.payerType} />
                <DisplayField label="Insurance / Payer Name" value={display?.provider} />
                <DisplayField label="Policy No." value={display?.policy} />
                <DisplayField label="Patient Name" value={patient?.name} />
                <DisplayField label="Admission (IPD) ID" value={admissionId} />
              </div>
            </div>

            {/* ── 2. ROUTING / COVERAGE ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionLabelStyle}>
                <SvgIcon name="send" size={14} color={C.textSub} /> Request
              </div>
              <div style={gridStyle}>
                <Field label="Payer (NHCX registry)" required>
                  <IpdTextField select fullWidth value={request.insurerKey} onChange={(e) => setReq("insurerKey", e.target.value)}>
                    {NHCX_INSURER_REGISTRY.map((i) => (
                      <MenuItem key={i.key} value={i.key}>{i.label}</MenuItem>
                    ))}
                    <MenuItem value="custom">Other — enter manually</MenuItem>
                  </IpdTextField>
                </Field>
                <Field label="Member / Subscriber ID" required>
                  <IpdTextField
                    fullWidth
                    value={request.coverage_subscriber_id}
                    onChange={(e) => setReq("coverage_subscriber_id", e.target.value)}
                    placeholder="Member ID on the card"
                    title="The member ID on the card — not the policy number."
                  />
                </Field>
                <Field
                  label="Policy Valid Until"
                  required
                  hint={isPastDate(request.coverage_period_end) ? "This date is in the past — the policy has expired." : ""}
                  hintTone={isPastDate(request.coverage_period_end) ? "warning" : undefined}
                >
                  <IpdDateField fullWidth value={request.coverage_period_end} onChange={(iso) => setReq("coverage_period_end", iso)} />
                </Field>
                <Field label="Relationship to Subscriber">
                  <IpdTextField
                    select
                    fullWidth
                    value={request.coverage_relationship_code}
                    onChange={(e) => setReq("coverage_relationship_code", e.target.value)}
                  >
                    {RELATIONSHIPS.map((r) => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </IpdTextField>
                </Field>
                {isClaim && (
                  <Field
                    label="Authorisation Reference"
                    required
                    hint={
                      request.pre_auth_ref === SANDBOX_PREAUTH_PLACEHOLDER
                        ? "Sandbox dummy payer issues no preAuthRef — this is a placeholder."
                        : "Filled from the approved pre-auth. Do not retype it."
                    }
                  >
                    <IpdTextField
                      fullWidth
                      value={request.pre_auth_ref}
                      onChange={(e) => setReq("pre_auth_ref", e.target.value)}
                      placeholder="PA-2026-0001"
                    />
                  </Field>
                )}
                <Field label="Hospital Claim Reference" hint="Omitted → the server generates a UUID4. Supply your real number in production.">
                  <IpdTextField
                    fullWidth
                    value={request.claim_identifier_value}
                    onChange={(e) => setReq("claim_identifier_value", e.target.value)}
                    placeholder="CLM-2024-00123"
                  />
                </Field>
                <Field label="Priority">
                  <IpdTextField select fullWidth value={request.priority} onChange={(e) => setReq("priority", e.target.value)}>
                    {PRIORITIES.map((p) => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </IpdTextField>
                </Field>
                <Field label="Sub-type" hint="Free text; omitted from the bundle if blank.">
                  <IpdTextField
                    fullWidth
                    value={request.sub_type}
                    onChange={(e) => setReq("sub_type", e.target.value)}
                    placeholder="emergency"
                  />
                </Field>
              </div>

              {request.insurerKey === "custom" ? (
                <div style={routingBoxStyle}>
                  <div style={{ ...sectionLabelStyle, marginBottom: "12px" }}>Payer routing</div>
                  <div style={gridStyle}>
                    <Field label="Payer Code" required hint="NHCX registry code, e.g. 1000003538@hcx">
                      <IpdTextField fullWidth value={customInsurer.payer_code} onChange={(e) => setCustomInsurer((p) => ({ ...p, payer_code: e.target.value }))} placeholder="1000003538@hcx" />
                    </Field>
                    <Field label="Insurer ROHINI ID" required hint="13 digits in production">
                      <IpdTextField fullWidth value={customInsurer.insurer_rohini_id} onChange={(e) => setCustomInsurer((p) => ({ ...p, insurer_rohini_id: e.target.value }))} />
                    </Field>
                    <Field label="Insurer Name">
                      <IpdTextField fullWidth value={customInsurer.insurer_name} onChange={(e) => setCustomInsurer((p) => ({ ...p, insurer_name: e.target.value }))} />
                    </Field>
                    <Field label="Insurer Phone" required hint="+91 is added automatically">
                      <IpdTextField fullWidth value={customInsurer.insurer_phone} onChange={(e) => setCustomInsurer((p) => ({ ...p, insurer_phone: e.target.value }))} placeholder="9876543210" />
                    </Field>
                    <Field label="Insurer Email" required>
                      <IpdTextField fullWidth value={customInsurer.insurer_email} onChange={(e) => setCustomInsurer((p) => ({ ...p, insurer_email: e.target.value }))} />
                    </Field>
                    <Field label="Policy Namespace URI" required hint="From the insurer's onboarding docs">
                      <IpdTextField fullWidth value={customInsurer.insurer_policy_namespace_uri} onChange={(e) => setCustomInsurer((p) => ({ ...p, insurer_policy_namespace_uri: e.target.value }))} placeholder="https://insurer.in/policy/" />
                    </Field>
                  </div>
                </div>
              ) : (
                <div style={routingBoxStyle}>
                  <div style={{ ...sectionLabelStyle, marginBottom: "12px" }}>Payer routing</div>
                  <div style={gridStyle}>
                    <DisplayField label="Payer Code" value={insurer.payer_code} />
                    <DisplayField label="ROHINI ID" value={insurer.insurer_rohini_id} />
                    <DisplayField label="Insurer Phone" value={insurer.insurer_phone} />
                    <DisplayField label="Insurer Email" value={insurer.insurer_email} />
                    <DisplayField label="Policy Namespace" value={insurer.insurer_policy_namespace_uri} />
                  </div>
                </div>
              )}
            </div>

            {/* ── 3. BILLABLE PERIOD ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionLabelStyle}>
                <SvgIcon name="calendar" size={14} color={C.textSub} /> Expected Treatment End
              </div>
              <div style={gridStyle}>
                <Field label="End date" required hint="Start comes from the admission record. Sent as IST (+05:30).">
                  <IpdDateField fullWidth value={request.billable_date} onChange={(iso) => setReq("billable_date", iso)} />
                </Field>
                <Field label="End time">
                  <IpdTextField
                    type="time"
                    fullWidth
                    value={request.billable_time}
                    onChange={(e) => setReq("billable_time", e.target.value)}
                  />
                </Field>
              </div>
            </div>

            {/* ── 4. DIAGNOSES ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionLabelStyle}>
                <SvgIcon name="flask" size={14} color={C.textSub} /> Diagnoses (ICD-10)
              </div>
              <div style={{ fontSize: "12px", color: C.textSub, marginBottom: "12px" }}>
                {mapSourceSnomed
                  ? "The first diagnosis is looked up from the selected SNOMED procedure or billing item."
                  : "Pick a SNOMED procedure or billing item to fill this from the map, or search ICD-10 yourself."}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {diagnoses.map((dx, idx) => (
                  <div key={idx} style={innerCardStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "12px", fontWeight: "700", color: C.text }}>Diagnosis {idx + 1}</span>
                      {diagnoses.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setDiagnoses((prev) => prev.filter((_, i) => i !== idx))}
                          style={{ border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", color: C.danger, fontSize: "12px", fontWeight: "600" }}
                        >
                          <SvgIcon name="trash" size={13} color={C.danger} /> Remove
                        </button>
                      )}
                    </div>
                    <div style={gridStyle}>
                      <Field
                        label="Diagnosis"
                        required
                        hint={idx === 0 && mapSourceSnomed ? "From snomed/map_icd using the selected SNOMED code" : "Search by name or code — pick a result"}
                      >
                        {idx === 0 && mapSourceSnomed ? (
                          <Icd10MappedField
                            snomedCode={mapSourceSnomed}
                            code={dx.icd10_code}
                            display={dx.icd10_display}
                            onChange={(row) => setDx(idx, { icd10_code: row.code, icd10_display: row.display })}
                          />
                        ) : (
                          <Icd10SearchField
                            term={dx.icd10_display}
                            code={dx.icd10_code}
                            placeholder="e.g. unstable angina or I20.0"
                            onTermChange={(value) => setDx(idx, { icd10_display: value, icd10_code: "" })}
                            onSelect={(row) => setDx(idx, { icd10_display: row.display, icd10_code: row.code })}
                          />
                        )}
                      </Field>
                      <DisplayField label="ICD-10 Code" value={dx.icd10_code} />
                    </div>
                  </div>
                ))}
                <div>
                  <IpdButton variant="outlined" onClick={() => setDiagnoses((prev) => [...prev, { ...EMPTY_DX }])} startIcon={<SvgIcon name="plus" size={14} color={C.primary} />}>
                    Add diagnosis
                  </IpdButton>
                </div>
              </div>
            </div>

            {/* ── 5. PROCEDURES (optional) ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionLabelStyle}>
                <SvgIcon name="pill" size={14} color={C.textSub} /> Procedures (SNOMED CT, optional)
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {procedures.map((px, idx) => (
                  <div key={idx} style={innerCardStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "12px", fontWeight: "700", color: C.text }}>Procedure {idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => setProcedures((prev) => prev.filter((_, i) => i !== idx))}
                        style={{ border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", color: C.danger, fontSize: "12px", fontWeight: "600" }}
                      >
                        <SvgIcon name="trash" size={13} color={C.danger} /> Remove
                      </button>
                    </div>
                    <div style={gridStyle}>
                      <Field label="Procedure" required hint="A half-filled row is rejected — pick a concept or remove the row">
                        <SnomedSearchField
                          term={px.snomed_display}
                          code={px.snomed_code}
                          semanticTag="procedure"
                          placeholder="e.g. stent placement"
                          onTermChange={(value) => setPx(idx, { snomed_display: value, snomed_code: "" })}
                          onSelect={(concept) => setPx(idx, { snomed_display: concept.term, snomed_code: concept.code })}
                        />
                      </Field>
                      <DisplayField label="SNOMED CT Code" value={px.snomed_code} />
                    </div>
                  </div>
                ))}
                <div>
                  <IpdButton variant="outlined" onClick={() => setProcedures((prev) => [...prev, { ...EMPTY_PX }])} startIcon={<SvgIcon name="plus" size={14} color={C.primary} />}>
                    Add procedure
                  </IpdButton>
                </div>
              </div>
            </div>

            {/* ── 6. BILLING ITEMS ── */}
            <div style={sectionBoxStyle}>
              <div style={{ ...sectionLabelStyle, justifyContent: "space-between" }}>
                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <SvgIcon name="bill" size={14} color={C.textSub} /> Billing Items
                </span>
                <span style={{ fontSize: "13px", fontWeight: "800", color: C.text, textTransform: "none", letterSpacing: 0 }}>
                  Running total {fmtINR(runningTotal)}
                </span>
              </div>
              <div style={{ fontSize: "12px", color: C.textSub, marginBottom: "12px" }}>
                The total is shown for you — it is not sent. The server computes net = unit price × quantity per line.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {items.map((item, idx) => (
                  <div key={idx} style={innerCardStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "12px", fontWeight: "700", color: C.text }}>
                        Item {idx + 1}
                        {toMoney(item.unit_price) > 0 && toMoney(item.quantity_value) > 0
                          ? ` · ${fmtINR(toMoney(toMoney(item.unit_price) * toMoney(item.quantity_value)))}`
                          : ""}
                      </span>
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                          style={{ border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", color: C.danger, fontSize: "12px", fontWeight: "600" }}
                        >
                          <SvgIcon name="trash" size={13} color={C.danger} /> Remove
                        </button>
                      )}
                    </div>
                    <div style={gridStyle}>
                      <Field label="Service" required hint="Search by name — the SNOMED code comes from the concept you pick">
                        <SnomedSearchField
                          term={item.product_or_service_snomed_display}
                          code={item.product_or_service_snomed_code}
                          semanticTag="procedure"
                          placeholder="e.g. stent placement"
                          onTermChange={(value) =>
                            setItem(idx, { product_or_service_snomed_display: value, product_or_service_snomed_code: "" })
                          }
                          onSelect={(concept) =>
                            setItem(idx, {
                              product_or_service_snomed_display: concept.term,
                              product_or_service_snomed_code: concept.code,
                            })
                          }
                        />
                      </Field>
                      <DisplayField label="SNOMED CT Code" value={item.product_or_service_snomed_code} />
                      <Field label="Unit Price (₹)" required>
                        <IpdTextField
                          type="number"
                          fullWidth
                          inputProps={{ min: 0.01, step: 0.01 }}
                          value={item.unit_price}
                          onChange={(e) => setItem(idx, { unit_price: e.target.value })}
                          placeholder="15000.00"
                        />
                      </Field>
                      <Field label="Quantity" required>
                        <IpdTextField
                          type="number"
                          fullWidth
                          inputProps={{ min: 0.01, step: 0.01 }}
                          value={item.quantity_value}
                          onChange={(e) => setItem(idx, { quantity_value: e.target.value })}
                          placeholder="5"
                        />
                      </Field>
                      <Field label="Quantity Unit" required>
                        <IpdTextField
                          select
                          fullWidth
                          value={item.quantity_unit}
                          onChange={(e) => setItem(idx, { quantity_unit: e.target.value })}
                        >
                          {QUANTITY_UNITS.map((u) => (
                            <MenuItem key={u} value={u}>{u}</MenuItem>
                          ))}
                        </IpdTextField>
                      </Field>
                      <Field label="Serviced Date" hint="Defaults to the admission date if omitted">
                        <IpdDateField fullWidth value={item.serviced_date} onChange={(iso) => setItem(idx, { serviced_date: iso })} />
                      </Field>
                    </div>
                  </div>
                ))}
                <div>
                  <IpdButton variant="outlined" onClick={() => setItems((prev) => [...prev, { ...EMPTY_ITEM }])} startIcon={<SvgIcon name="plus" size={14} color={C.primary} />}>
                    Add billing item
                  </IpdButton>
                </div>
              </div>
            </div>

            {/* ── 7. SUPPORTING INFO (optional, text only) ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionLabelStyle}>
                <SvgIcon name="invoice" size={14} color={C.textSub} /> Supporting Info (text only, optional)
              </div>
              <div style={{ fontSize: "12px", color: C.textSub, marginBottom: "12px" }}>
                Document attachments go through the Communication flow when the payer asks for them — not here.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {supporting.map((row, idx) => (
                  <div key={idx} style={innerCardStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "12px", fontWeight: "700", color: C.text }}>Note {idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => setSupporting((prev) => prev.filter((_, i) => i !== idx))}
                        style={{ border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", color: C.danger, fontSize: "12px", fontWeight: "600" }}
                      >
                        <SvgIcon name="trash" size={13} color={C.danger} /> Remove
                      </button>
                    </div>
                    <div style={gridStyle}>
                      <Field label="Category" required>
                        <IpdTextField
                          select
                          fullWidth
                          value={row.category}
                          onChange={(e) =>
                            setSupporting((prev) => prev.map((s, i) => (i === idx ? { ...s, category: e.target.value } : s)))
                          }
                        >
                          {SUPPORT_CATEGORIES.map((c) => (
                            <MenuItem key={c} value={c}>{c}</MenuItem>
                          ))}
                        </IpdTextField>
                      </Field>
                      <Field label="Value">
                        <IpdTextField
                          fullWidth
                          value={row.value_string}
                          onChange={(e) =>
                            setSupporting((prev) =>
                              prev.map((s, i) => (i === idx ? { ...s, value_string: e.target.value } : s))
                            )
                          }
                          placeholder="Haemoglobin: 11.2 g/dL"
                        />
                      </Field>
                    </div>
                  </div>
                ))}
                <div>
                  <IpdButton variant="outlined" onClick={() => setSupporting((prev) => [...prev, { ...EMPTY_INFO }])} startIcon={<SvgIcon name="plus" size={14} color={C.primary} />}>
                    Add supporting info
                  </IpdButton>
                </div>
              </div>
            </div>

            {/* ── 8. OVERRIDES — revealed only when the server says they're missing ── */}
            {revealed.length > 0 && (
              <div style={sectionBoxStyle}>
                <div style={sectionLabelStyle}>
                  <SvgIcon name="alert-triangle" size={14} color={C.warning} /> Additional Details Required
                </div>
                <div style={{ fontSize: "12px", color: C.textSub, marginBottom: "12px" }}>
                  The hospital record has no value for these, so NHCX cannot be given a complete bundle.
                  Aadhaar and ABHA count as one requirement — either one is enough. Do not send an ABHA Address.
                </div>
                <div style={gridStyle}>
                  {revealed.map((name) => {
                    const meta = OVERRIDE_FIELDS[name] || { label: name };
                    const setOverride = (value) => setOverrides((p) => ({ ...p, [name]: value }));
                    if (meta.type === "datetime") {
                      const parsed = parseIstIso(overrides[name]);
                      return (
                        <Field key={name} label={meta.label} hint={meta.hint}>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <IpdDateField
                              fullWidth
                              value={parsed.date}
                              onChange={(iso) => setOverride(toIstIso(iso, parsed.time))}
                            />
                            <IpdTextField
                              type="time"
                              value={parsed.time}
                              onChange={(e) => setOverride(toIstIso(parsed.date, e.target.value))}
                              sx={{ width: 140 }}
                            />
                          </div>
                        </Field>
                      );
                    }
                    return (
                      <Field key={name} label={meta.label} hint={meta.hint}>
                        {meta.type === "gender" ? (
                          <IpdTextField select fullWidth value={overrides[name] || ""} onChange={(e) => setOverride(e.target.value)}>
                            {GENDERS.map((g) => (
                              <MenuItem key={g} value={g}>{g}</MenuItem>
                            ))}
                          </IpdTextField>
                        ) : meta.type === "date" ? (
                          <IpdDateField fullWidth value={overrides[name] || ""} onChange={setOverride} maxDate={new Date()} />
                        ) : (
                          <IpdTextField fullWidth value={overrides[name] || ""} onChange={(e) => setOverride(e.target.value)} />
                        )}
                      </Field>
                    );
                  })}
                </div>
              </div>
            )}

            {error && <ErrorBlock error={error} />}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", flexWrap: "wrap" }}>
              <IpdButton variant="outlined" onClick={handleSkip} disabled={submitting}>
                Skip — preview next screens
              </IpdButton>
              <IpdButton
                onClick={handleSubmit}
                disabled={submitting}
                loading={submitting}
                startIcon={<SvgIcon name="send" size={15} color="#fff" />}
              >
                {submitting
                  ? "Submitting…"
                  : error && revealed.length
                    ? "Retry with details"
                    : isClaim
                      ? "Submit Claim"
                      : "Submit Pre-Authorization"}
              </IpdButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── phase: waiting ───────────────────────────────────────────────────────────

function WaitingState({
  mode,
  correlationId,
  apiCallId,
  attempts,
  exhausted,
  error,
  submittedAt,
  showDummyPayer,
  preview,
  onKeepWaiting,
  onSkipToResult,
  onStop,
}) {
  const [now, setNow] = useState(Date.now());
  const [triggering, setTriggering] = useState("");
  const [triggerMsg, setTriggerMsg] = useState("");

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const waitLeft = Math.max(0, DUMMY_PAYER_WAIT_S - Math.floor((now - (submittedAt || now)) / 1000));
  const dummyMethod = mode === "claim" ? "Claim" : "Preauth";

  const poke = async (action) => {
    setTriggering(action);
    setTriggerMsg("");
    try {
      await triggerDummyPayer({ action, method: dummyMethod, correlationId });
      setTriggerMsg(`Dummy payer ${action} sent — keep waiting for the reply.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const text =
        (typeof detail === "string" && detail) ||
        detail?.message ||
        err?.message ||
        "Dummy payer trigger failed.";
      setTriggerMsg(text);
    } finally {
      setTriggering("");
    }
  };

  return (
    <div style={{ ...innerCardStyle, alignItems: "center", textAlign: "center", padding: "40px 20px", gap: "10px" }}>
      {preview && <PreviewBanner />}
      <SvgIcon name={exhausted ? "clock" : "sync"} size={26} color={C.primary} />
      <div style={{ fontSize: "15px", fontWeight: "700", color: C.text }}>
        {exhausted ? "Still no answer from the payer" : "Submitted to NHCX — waiting for the payer"}
      </div>
      <div style={{ fontSize: "13px", color: C.textSub, maxWidth: "460px" }}>
        {exhausted
          ? "The payer replies asynchronously and can take longer than a minute. Nothing is lost — keep waiting or come back and check again."
          : `The payer answers asynchronously. Checked ${attempts} time${attempts === 1 ? "" : "s"}…`}
      </div>
      <div style={{ fontSize: "11px", color: C.textSub, fontFamily: "monospace", marginTop: "4px" }}>
        correlation id · {correlationId}
      </div>
      {apiCallId && (
        <div style={{ fontSize: "11px", color: C.textSub, fontFamily: "monospace" }}>
          api call id · {apiCallId}
        </div>
      )}
      {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error.message}</div>}

      {showDummyPayer && (
        <div
          style={{
            ...innerCardStyle,
            width: "100%",
            maxWidth: "520px",
            marginTop: "8px",
            textAlign: "left",
            background: "#fff8e1",
            borderColor: "#ffe69c",
          }}
        >
          <div style={{ fontSize: "12px", fontWeight: "800", color: C.text }}>Sandbox dummy payer</div>
          <div style={{ fontSize: "12px", color: C.textSub }}>
            Nothing happens after submit until you trigger a decision. Wait ~{DUMMY_PAYER_WAIT_S} seconds
            first — too soon returns SBX-004 (request not queued yet).
            {waitLeft > 0 ? ` Ready in ${waitLeft}s.` : ""}
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {["Approve", "Reject", "Query"].map((action) => (
              <IpdButton
                key={action}
                variant={action === "Approve" ? undefined : "outlined"}
                disabled={waitLeft > 0 || Boolean(triggering)}
                loading={triggering === action}
                onClick={() => poke(action)}
              >
                {action}
              </IpdButton>
            ))}
          </div>
          {triggerMsg && (
            <div style={{ fontSize: "12px", color: /fail|SBX-|error/i.test(triggerMsg) ? C.danger : C.text }}>
              {triggerMsg}
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: "10px", marginTop: "8px", flexWrap: "wrap", justifyContent: "center" }}>
        <IpdButton variant="outlined" onClick={onSkipToResult}>
          Skip to result
        </IpdButton>
        {exhausted && (
          <IpdButton onClick={onKeepWaiting} startIcon={<SvgIcon name="refresh" size={14} color="#fff" />}>
            Keep waiting
          </IpdButton>
        )}
        {(exhausted || preview) && (
          <IpdButton variant="outlined" onClick={onStop}>Start over</IpdButton>
        )}
      </div>
    </div>
  );
}

// ── phase: result ────────────────────────────────────────────────────────────

function ResultState({
  mode,
  result,
  correlationId,
  apiCallId,
  preAuthRef,
  preview,
  onRunAgain,
  onContinueToClaim,
  onContinueToCommunication,
  onCopyPreauthToManual,
  onCopyClaimToManual,
  onKeepPolling,
}) {
  const status = result?.status;
  const stage = result?.stage;
  const decision = result?.decision || {};
  const isClaim = mode === "claim";
  const isQuery =
    stage === "query_raised" || decision.kind === "query_received";
  const issuedRef = decision.pre_auth_ref || preAuthRef || "";
  const sandboxMissingRef = Boolean(stage === "approved" && !decision.pre_auth_ref);
  const eligible = eligibleTotal(decision);
  const asks = payloadStringsOf(result);

  if (status && status !== "complete" && status !== "partial") {
    const details = result?.error_details;
    const message =
      status === "decryption_error"
        ? "The server could not decrypt the payer's reply. This needs support — it is not something you can fix here."
        : status === "protocol_error"
          ? "NHCX or the payer could not process the request. Quote the correlation id to support."
          : (typeof details === "string" && details) ||
            details?.message ||
            "The payer returned an error for this request.";
    return (
      <div style={{ ...innerCardStyle, gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SvgIcon name="alert-triangle" size={16} color={C.danger} />
          <span style={{ fontSize: "14px", fontWeight: "700", color: C.text }}>
            {mode === "claim" ? "Claim failed" : "Pre-authorization failed"}
          </span>
          <Pill tone="red">{status}</Pill>
        </div>
        <div style={{ fontSize: "13px", color: C.textSub }}>{message}</div>
        <IdsBlock correlationId={correlationId} apiCallId={apiCallId} receivedAt={result?.received_at} />
        <div>
          <IpdButton variant="outlined" onClick={onRunAgain}>Submit again</IpdButton>
        </div>
      </div>
    );
  }

  if (isQuery) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: "16px", alignItems: "start" }}>
        <div style={innerCardStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Payer query
            </span>
            <Pill tone="amber">query_raised</Pill>
          </div>
          <div style={{ fontSize: "18px", fontWeight: "800", color: C.text }}>The payer wants more information</div>
          <div style={{ fontSize: "13px", color: C.textSub }}>
            Reply on the Communication step. The document request lands under its own
            correlation id; the final decision still arrives here afterwards.
          </div>
          {asks.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "13px", color: C.text }}>
              {asks.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          )}
        </div>
        <div style={innerCardStyle}>
          <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Next
          </span>
          {onContinueToCommunication && (
            <IpdButton onClick={onContinueToCommunication} startIcon={<SvgIcon name="send" size={14} color="#fff" />}>
              Reply on Communication
            </IpdButton>
          )}
          <IpdButton
            variant={onContinueToCommunication ? "outlined" : undefined}
            onClick={onKeepPolling}
            startIcon={<SvgIcon name="refresh" size={14} color={onContinueToCommunication ? C.primary : "#fff"} />}
          >
            Keep polling for the decision
          </IpdButton>
          <IpdButton variant="outlined" onClick={onRunAgain}>Start over</IpdButton>
          <IdsBlock correlationId={correlationId} apiCallId={apiCallId} receivedAt={result?.received_at} />
        </div>
      </div>
    );
  }

  const approved = stage === "approved";
  const denied = stage === "denied";

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: "16px", alignItems: "start" }}>
      {preview && (
        <div style={{ gridColumn: "1 / -1" }}>
          <PreviewBanner />
        </div>
      )}
      <div style={innerCardStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Decision
          </span>
          <Pill tone={stageTone(stage || status)}>{stageLabel(mode, stage) || status || "complete"}</Pill>
        </div>
        <div style={{ fontSize: "20px", fontWeight: "800", color: approved ? C.success : denied ? C.danger : C.text }}>
          {approved ? (mode === "claim" ? "Approved for settlement" : "Pre-authorization approved") : denied ? "Rejected" : (stageLabel(mode, stage) || "Payer answered")}
        </div>
        {decision.outcome && (
          <div style={{ fontSize: "13px", color: C.textSub }}>Outcome · {decision.outcome}</div>
        )}
        {decision.disposition && (
          <div style={{ fontSize: "13px", color: C.textSub }}>{decision.disposition}</div>
        )}
        {(issuedRef || sandboxMissingRef) && (
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", borderTop: `1px solid ${C.border}`, paddingTop: "10px", gap: "10px" }}>
            <span style={{ color: C.textSub }}>Authorisation ref</span>
            <span style={{ fontWeight: "700", color: C.text, fontFamily: "monospace" }}>
              {issuedRef || "not issued (sandbox)"}
            </span>
          </div>
        )}
        {sandboxMissingRef && (
          <div style={{ fontSize: "12px", color: C.warning }}>
            The dummy payer returns no preAuthRef. Claim submission will use a placeholder until a real payer issues one.
          </div>
        )}
        {eligible != null && Number.isFinite(eligible) && (
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
            <span style={{ color: C.textSub }}>Eligible amount</span>
            <span style={{ fontWeight: "800", color: C.text }}>{fmtINR(eligible)}</span>
          </div>
        )}
      </div>

      <div style={innerCardStyle}>
        <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Adjudication
        </span>
        {(decision.total || []).length === 0 && (decision.items || []).length === 0 ? (
          <div style={{ fontSize: "13px", color: C.textSub }}>The payer returned no itemised amounts.</div>
        ) : (
          <>
            {(decision.total || []).map((t, i) => (
              <div key={`t-${i}`} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                <span style={{ color: C.textSub }}>{t.category || "total"}</span>
                <span style={{ fontWeight: "700", color: C.text }}>{fmtINR(t.amount)}</span>
              </div>
            ))}
            {(decision.items || []).map((it, i) => {
              const adj = (it.adjudication || [])[0];
              return (
                <div key={`i-${i}`} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", borderTop: `1px solid ${C.border}`, paddingTop: "8px" }}>
                  <span style={{ color: C.textSub }}>Line {it.sequence ?? i + 1} · {adj?.category || "adjudication"}</span>
                  <span style={{ fontWeight: "700", color: C.text }}>{adj?.amount != null ? fmtINR(adj.amount) : "—"}</span>
                </div>
              );
            })}
          </>
        )}
        {(decision.errors || []).length > 0 && (
          <div style={{ fontSize: "12px", color: C.danger, borderTop: `1px solid ${C.border}`, paddingTop: "10px" }}>
            <div style={{ fontWeight: "700", marginBottom: "4px" }}>Reported by the payer ({decision.errors.length})</div>
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              {decision.errors.map((e, i) => (
                <li key={i}>{typeof e === "string" ? e : e?.message || JSON.stringify(e)}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div style={innerCardStyle}>
        <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Next
        </span>
        <div style={{ fontSize: "12px", color: C.textSub }}>
          NHCX results are held in the service's memory and are lost when it restarts — copy what you need onto this admission.
        </div>
        {approved && !isClaim && onContinueToClaim && (
          <IpdButton onClick={onContinueToClaim} startIcon={<SvgIcon name="arrow-right" size={14} color="#fff" />}>
            Continue to Claim
          </IpdButton>
        )}
        {approved && !isClaim && onCopyPreauthToManual && (
          <IpdButton
            variant="outlined"
            onClick={() => onCopyPreauthToManual(issuedRef || SANDBOX_PREAUTH_PLACEHOLDER)}
            startIcon={<SvgIcon name="check" size={14} color={C.primary} />}
          >
            Copy to Manual
          </IpdButton>
        )}
        {approved && isClaim && onContinueToCommunication && (
          <IpdButton onClick={onContinueToCommunication} startIcon={<SvgIcon name="arrow-right" size={14} color="#fff" />}>
            Continue to Communication
          </IpdButton>
        )}
        {approved && isClaim && onCopyClaimToManual && eligible != null && (
          <IpdButton
            variant={onContinueToCommunication ? "outlined" : undefined}
            onClick={() => onCopyClaimToManual(eligible)}
            startIcon={<SvgIcon name="check" size={14} color={onContinueToCommunication ? C.primary : "#fff"} />}
          >
            Copy approved amount to Manual
          </IpdButton>
        )}
        <IpdButton variant="outlined" onClick={onRunAgain}>
          {isClaim ? "Submit another claim" : "Submit another pre-auth"}
        </IpdButton>
        <IdsBlock correlationId={correlationId} apiCallId={apiCallId} receivedAt={result?.received_at} />
      </div>
    </div>
  );
}

function IdsBlock({ correlationId, apiCallId, receivedAt }) {
  return (
    <div style={{ fontSize: "10px", color: C.textSub, fontFamily: "monospace", wordBreak: "break-all", borderTop: `1px solid ${C.border}`, paddingTop: "10px" }}>
      <div>correlation · {correlationId}</div>
      {apiCallId && <div>api call · {apiCallId}</div>}
      {receivedAt && <div>received · {receivedAt}</div>}
    </div>
  );
}

function ErrorBlock({ error }) {
  return (
    <div style={{ border: `1px solid #f5c2c0`, background: "#fdecec", borderRadius: "10px", padding: "14px 16px", display: "flex", flexDirection: "column", gap: "8px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <SvgIcon name="alert-triangle" size={15} color={C.danger} />
        <span style={{ fontSize: "13px", fontWeight: "700", color: C.danger }}>{error.message}</span>
      </div>
      {error.fieldErrors?.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "12px", color: C.text }}>
          {error.fieldErrors.map((f, i) => (
            <li key={i}><strong>{f.field}</strong> — {f.message}</li>
          ))}
        </ul>
      )}
      {error.missing?.filter((m) => !m.overrides.length).map((m, i) => (
        <div key={i} style={{ fontSize: "12px", color: C.text }}>
          {m.message} <strong>Fix this on the source record in the hospital system.</strong>
        </div>
      ))}
    </div>
  );
}
